import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { isOfMetaOrigin } from "../utils/marketing-url-params.js";
import useUserAgent from "./useUserAgent.js";
import { EUROPEAN_COUNTRIES } from "../constants.js";
import { EEA_COUNTRY_CODE_LIST, LATAM_COUNTRIES } from "@flights/constants";
import { INFANT_MAX_AGE } from "@flights/constants";
import useGlobalContext from "./useGlobalContext.js";
import useRouteName from "./useRouteName.js";
import { differenceInCalendarDays } from "date-fns";
import { getOrderUtils } from "../utils/getOrderUtils.js";
import { isVirtualInterliningFlight } from "./useVirutalInterlining.js";
import { getOrderSlice } from "../store/order/selectors.js";
import { getFlightDetailsSlice } from "../store/flightDetails/selectors.js";
import { getSearchCriteria } from "../store/searchCriteria/selectors.js";
import { getUserSlice } from "../store/user/selectors.js";
import { getSearch } from "../store/search/selectors.js";
import { getCartDetails as getCartDetailsData } from "../store/cartDetails/selectors.js";
import { getSearchBoxState } from "../store/searchBoxState/selectors.js";
import { getSearchResultsSlice } from "../store/searchResults/selectors.js";
const parseDate = (s) => new Date(s);
const dateDiffInDays = (s, e) => {
    try {
        const start = parseDate(s);
        const end = parseDate(e);
        return differenceInCalendarDays(end, start);
    }
    catch (e) {
        return undefined;
    }
};
export default function useProfiler() {
    const routeName = useRouteName();
    const { isMobile: _isMobile } = useUserAgent();
    const { ipCountry, locale } = useGlobalContext();
    const order = useSelector(getOrderSlice);
    const flightDetails = useSelector(getFlightDetailsSlice);
    const searchCriteria = useSelector(getSearchCriteria);
    const user = useSelector(getUserSlice);
    const search = useSelector(getSearch);
    const cartDetails = useSelector(getCartDetailsData);
    const searchBoxState = useSelector(getSearchBoxState);
    const searchResults = useSelector(getSearchResultsSlice);
    const ip = useMemo(() => {
        if (["checkout-payment", "confirmation"].includes(routeName) || routeName.startsWith("pb-")) {
            return order?.order?.salesInfo?.ipCountry?.toLowerCase() || "";
        }
        else {
            return ipCountry?.toLowerCase() || "";
        }
    }, [routeName, order, ipCountry]);
    const tripType = useMemo(() => {
        const flightTripType = flightDetails?.flight?.tripType;
        const searchTripType = search?.type;
        return order?.order ? getOrderUtils(order.order).tripType : flightTripType || searchTripType || "";
    }, [flightDetails, search, order]);
    const personalisationSegments = useMemo(() => {
        return flightDetails?.flight?.personalisationSegments?.length
            ? flightDetails.flight.personalisationSegments
            : searchResults?.personalisationSegments?.length
                ? searchResults.personalisationSegments
                : [];
    }, [searchResults, flightDetails]);
    const travellersFromSearchCriteria = useMemo(() => {
        const travellers = [];
        const adults = searchCriteria?.adults || 1;
        Array.from({ length: adults }).forEach(() => {
            travellers.push({ travellerReference: "", type: "ADULT" });
        });
        searchCriteria?.children?.forEach((age) => {
            travellers.push({ travellerReference: "", type: "KID", age: Number(age) });
        });
        return travellers;
    }, [searchCriteria]);
    const travellersFromSearchBoxState = useMemo(() => {
        const travellers = [];
        const adults = searchBoxState?.adults || 1;
        Array.from({ length: adults }).forEach(() => {
            travellers.push({ travellerReference: "", type: "ADULT" });
        });
        searchBoxState?.children?.forEach((age) => {
            travellers.push({ travellerReference: "", type: "KID", age });
        });
        return travellers;
    }, [searchBoxState]);
    const passengers = useMemo(() => !!order?.order?.passengers?.length
        ? order.order.passengers
        : !!cartDetails?.passengers?.length
            ? cartDetails.passengers
            : !!flightDetails?.flight?.travellers?.length
                ? flightDetails.flight.travellers
                : travellersFromSearchCriteria.length
                    ? travellersFromSearchCriteria
                    : travellersFromSearchBoxState.length
                        ? travellersFromSearchBoxState
                        : [], [order, flightDetails, cartDetails, travellersFromSearchCriteria, travellersFromSearchBoxState]);
    const segments = useMemo(() => {
        return !!order?.order?.airOrder?.flightSegments?.length
            ? order.order.airOrder.flightSegments
            : !!cartDetails?.cart?.offer?.segments?.length
                ? cartDetails.cart.offer.segments
                : !!flightDetails?.flight?.segments?.length
                    ? flightDetails.flight.segments
                    : [];
    }, [order, flightDetails, cartDetails]);
    const firstDepartureDay = useMemo(() => {
        const t = segments?.[0]?.departureTimeTz;
        return !t ? undefined : t;
    }, [segments]);
    const bookingDay = useMemo(() => {
        return order?.order?.airOrder?.bookedAt || new Date().toISOString();
    }, [order]);
    const noOfAdults = useMemo(() => {
        const adults = passengers.filter(({ type }) => type === "ADULT");
        return adults.length || 1;
    }, [passengers]);
    const noOfChildren = useMemo(() => {
        const children = passengers.filter(({ type, age }) => type === "KID" && ((age !== undefined && age > INFANT_MAX_AGE) || age === undefined));
        return children.length || 0;
    }, [passengers]);
    const noOfInfants = useMemo(() => {
        const infants = passengers.filter(({ type, age }) => type === "KID" && age !== undefined && age <= INFANT_MAX_AGE);
        return infants.length || 0;
    }, [passengers]);
    const isMeta = useCallback(() => !!isOfMetaOrigin(), []);
    const isDirect = useCallback(() => !isOfMetaOrigin(), []);
    const isMobile = useCallback(() => !!_isMobile, [_isMobile]);
    const isDesktop = useCallback(() => !_isMobile, [_isMobile]);
    const isSolo = useCallback(() => noOfAdults === 1 && noOfChildren === 0 && noOfInfants === 0, [noOfAdults, noOfChildren, noOfInfants]);
    const isCouple = useCallback(() => noOfAdults === 2 && noOfChildren === 0 && noOfInfants === 0, [noOfAdults, noOfChildren, noOfInfants]);
    const isGroup = useCallback(() => noOfAdults > 2 && (noOfChildren >= 0 || noOfInfants >= 0), [noOfAdults, noOfChildren, noOfInfants]);
    const isFamily = useCallback(() => noOfAdults > 0 && (noOfChildren > 0 || noOfInfants > 0), [noOfAdults, noOfChildren, noOfInfants]);
    const isWithInfants = useCallback(() => !!noOfInfants, [noOfInfants]);
    const isWithKids = useCallback(() => !!noOfChildren, [noOfChildren]);
    const travellersCount = useCallback(() => noOfAdults + noOfChildren + noOfInfants, [noOfAdults, noOfChildren, noOfInfants]);
    const isLoggedIn = useCallback(() => !!user?.userInfo?.email, [user]);
    const isLoggedInAuthLow = useCallback(() => !!user?.userInfo?.authLevel, [user]);
    const isLoggedOut = useCallback(() => !user?.userInfo?.email, [user]);
    const isGenius = useCallback(() => !!user?.userInfo?.geniusLevel && user?.userInfo?.geniusLevel > 0, [user]);
    const isHVC = useCallback(() => false, []);
    const isInternational = useCallback(() => {
        if (segments.length)
            return segments.some((_) => _.departureAirport.country !== _.arrivalAirport.country);
        return !!searchCriteria?.toCountry?.some((_) => searchCriteria.fromCountry[0] !== _);
    }, [segments, searchCriteria]);
    const isDomestic = useCallback(() => {
        if (segments.length)
            return segments.every((_) => _.departureAirport.country === _.arrivalAirport.country);
        return !!searchCriteria?.toCountry?.every((_) => searchCriteria.fromCountry?.[0] === _);
    }, [segments, searchCriteria]);
    const isRountrip = useCallback(() => tripType === "ROUNDTRIP", [tripType]);
    const isMultiStop = useCallback(() => tripType === "MULTISTOP", [tripType]);
    const isOneWay = useCallback(() => tripType === "ONEWAY", [tripType]);
    const isShortHaul = useCallback(() => {
        return personalisationSegments.includes("SHORT_HAUL_TRIP");
    }, [personalisationSegments]);
    const isMediumHaul = useCallback(() => {
        return personalisationSegments.includes("MEDIUM_HAUL_TRIP");
    }, [personalisationSegments]);
    const isLongHaul = useCallback(() => {
        return personalisationSegments.includes("LONG_HAUL_TRIP");
    }, [personalisationSegments]);
    const isWeekendStay = useCallback(() => {
        const segmentsStart = segments?.[0]?.departureTime;
        const segmentsEnd = segments?.[1]?.departureTime;
        const searchStart = search?.searchSegments?.[0]?.departureDate;
        const searchEnd = search?.searchSegments?.[0]?.returnDate;
        const start = segmentsStart || searchStart;
        const end = segmentsEnd || searchEnd;
        if (!start || !end)
            return false;
        const diff = dateDiffInDays(start, end);
        if (diff === undefined || diff < 2 || diff > 4)
            return false;
        let includesSaturday = false;
        let includesMonday = false;
        for (let day = parseDate(start); day <= parseDate(end); day.setDate(day.getDate() + 1)) {
            const dayOfWeek = Number(day.getDay());
            if (dayOfWeek === 6)
                includesSaturday = true;
            if (dayOfWeek === 1)
                includesMonday = true;
        }
        return includesSaturday && includesMonday;
    }, [segments, search]);
    const isLongStay = useCallback(() => {
        if (segments.length < 2)
            return false;
        const start = segments?.[0]?.departureTime;
        const end = segments?.[1]?.departureTime;
        const diff = dateDiffInDays(start, end);
        if (diff === undefined)
            return false;
        return diff !== undefined && diff >= 7;
    }, [segments]);
    const isOneDay = useCallback(() => {
        if (segments.length < 2)
            return false;
        const start = segments?.[0]?.departureTime;
        const end = segments?.[1]?.departureTime;
        const diff = dateDiffInDays(start, end);
        return diff !== undefined && diff <= 1;
    }, [segments]);
    const isVirtualInterlining = useCallback(() => {
        return isVirtualInterliningFlight(segments);
    }, [segments]);
    const isEarlyBird = useCallback(() => {
        if (!firstDepartureDay)
            return false;
        const diff = dateDiffInDays(bookingDay, firstDepartureDay);
        if (diff == undefined)
            return false;
        return diff > 31;
    }, [firstDepartureDay, bookingDay]);
    const isLateBird = useCallback(() => {
        if (!firstDepartureDay)
            return false;
        const diff = dateDiffInDays(bookingDay, firstDepartureDay);
        if (diff == undefined)
            return false;
        return diff >= 7 && diff <= 31;
    }, [firstDepartureDay, bookingDay]);
    const isLastMinute = useCallback(() => {
        if (!firstDepartureDay)
            return false;
        const diff = dateDiffInDays(bookingDay, firstDepartureDay);
        if (diff == undefined)
            return false;
        return diff < 7;
    }, [firstDepartureDay, bookingDay]);
    const daysToDeparture = useCallback(() => {
        if (!firstDepartureDay)
            return 0;
        const currentDate = new Date();
        const diff = dateDiffInDays(currentDate.toISOString(), firstDepartureDay);
        if (diff === undefined)
            return 0;
        return diff;
    }, [firstDepartureDay]);
    const isSensitivePrice = useCallback(() => false, []);
    const isSensitiveDate = useCallback(() => false, []);
    const isSensitiveTime = useCallback(() => false, []);
    const isAmerican = useCallback((code) => {
        if (code !== undefined)
            return code === "us";
        return ip === "us";
    }, [ip]);
    const isEuropean = useCallback((code) => {
        if (code !== undefined)
            return EUROPEAN_COUNTRIES.includes(code);
        return EUROPEAN_COUNTRIES.includes(ip);
    }, [ip]);
    const isEEA = useCallback((code) => {
        if (code !== undefined)
            return EEA_COUNTRY_CODE_LIST.includes(code);
        return EEA_COUNTRY_CODE_LIST.includes(ip);
    }, [ip]);
    const isLATAM = useCallback((code) => {
        if (code !== undefined)
            return LATAM_COUNTRIES.includes(code);
        return LATAM_COUNTRIES.includes(ip);
    }, [ip]);
    const isGlobal = useCallback((code) => {
        if (code !== undefined)
            return !isEuropean(code) && !isAmerican(code);
        return !!ip && !isEuropean() && !isAmerican();
    }, [isEuropean, isAmerican, ip]);
    const isPointOfSale = useCallback((code) => ip === code.toLowerCase(), [ip]);
    const isEnglish = useCallback(() => ["en", "en-us", "en-gb"].includes(locale?.toLocaleLowerCase() || ""), [locale]);
    const isPb = useCallback(() => routeName.startsWith("pb-"), [routeName]);
    const isOneLeg = useCallback(() => {
        const totalLegs = segments.reduce((count, segment) => count + (segment.legs?.length || 0), 0);
        return totalLegs === 1;
    }, [segments]);
    const isOneOrTwoLegs = useCallback(() => {
        const totalLegs = segments.reduce((count, segment) => count + (segment.legs?.length || 0), 0);
        return totalLegs === 1 || totalLegs === 2;
    }, [segments]);
    return {
        isAmerican,
        isCouple,
        isDesktop,
        isDirect,
        isDomestic,
        isEarlyBird,
        isEuropean,
        isEEA,
        isShortHaul,
        isMediumHaul,
        isLongHaul,
        isFamily,
        isGenius,
        isGlobal,
        isEnglish,
        isGroup,
        isInternational,
        isVirtualInterlining,
        isLastMinute,
        isLateBird,
        isLoggedIn,
        isLoggedInAuthLow,
        isLoggedOut,
        isLongStay,
        isMeta,
        isMobile,
        isMultiStop,
        isOneDay,
        isOneWay,
        isLATAM,
        isPointOfSale,
        isRountrip,
        isSolo,
        isWeekendStay,
        daysToDeparture,
        isWithInfants,
        isWithKids,
        isPb,
        noOfAdults,
        travellersCount,
        isOneLeg,
        isOneOrTwoLegs
    };
}
